'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Employee } from './types';

interface CompensationEntryModalProps {
  employee: Employee | null;
  onClose: () => void;
  onSaved: () => void;
}

const PAY_TYPES = [
  { key: 'salary',     label: 'Salary' },
  { key: 'hourly',     label: 'Hourly' },
  { key: 'commission', label: 'Commission' },
  { key: 'piece_rate', label: 'Piece Rate' },
  { key: 'contractor', label: 'Contractor' },
];

const CHANGE_REASONS = [
  'Annual merit increase', 'Promotion', 'Cost-of-living adjustment',
  'Market adjustment', 'Performance-based raise', 'Role change', 'Initial hire', 'Other',
];

const CompensationEntryModal = ({ employee, onClose, onSaved }: CompensationEntryModalProps) => {
  const [effectiveDate, setEffectiveDate] = useState('');
  const [salaryBand, setSalaryBand] = useState('');
  const [payType, setPayType] = useState('salary');
  const [changeReason, setChangeReason] = useState('Annual merit increase');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const supabase = createClient();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!employee) return;
    setSubmitting(false);
    setEffectiveDate(new Date().toISOString().slice(0, 10));
    setSalaryBand(''); setPayType('salary'); setChangeReason('Annual merit increase'); setNotes('');
  }, [employee?.id]);

  useEffect(() => {
    if (!employee) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [employee, onClose]);

  if (!employee) return null;

  const isValid = !!effectiveDate && salaryBand.trim().length >= 2 && !!payType && !submitting;

  const saveWithRetry = async () => {
    if (!isValid) return;
    setSubmitting(true);
    const approverName = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim() || 'Unknown';
    const payload: any = {
      employee_id: employee.id,
      effective_date: effectiveDate,
      salary_band: salaryBand.trim(),
      pay_type: payType,
      change_reason: changeReason,
      approved_by_name: approverName,
      notes: notes.trim() || null,
    };
    console.log(`[Compensation] Saving entry:`, payload);

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Save timed out (attempt ${attempt + 1}/3)`)), 8000));
      const opPromise = supabase.from('employee_compensation_history').insert(payload)
        .then((res: any) => { if (res.error) throw res.error; return res.data; });

      try {
        await Promise.race([opPromise, timeoutPromise]);
        await supabase.from('employees').update({ salary_band: salaryBand.trim(), updated_at: new Date().toISOString() }).eq('id', employee.id);
        console.log(`[Compensation] Save succeeded attempt ${attempt + 1}`);
        toast.success('Compensation entry added', { duration: 2500, icon: '💼' });
        onSaved();
        setSubmitting(false);
        onClose();
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[Compensation] Attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Save failed: ${msg}`, { duration: 6000 });
          setSubmitting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-[#1C2E5E]">Add Compensation Entry</h3>
            <p className="text-[12px] text-gray-500 mt-0.5">{employee.first_name} {employee.last_name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg>
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[10px] text-gray-400 italic">Salary band labels only. Actual amounts stay in your payroll provider.</p>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Effective Date <span className="text-red-500">*</span></label>
            <input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Salary Band <span className="text-red-500">*</span></label>
            <input type="text" value={salaryBand} onChange={e => setSalaryBand(e.target.value)} placeholder="e.g. Band 5, Senior IC, M3" maxLength={60} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Pay Type <span className="text-red-500">*</span></label>
            <select value={payType} onChange={e => setPayType(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all">
              {PAY_TYPES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Change Reason</label>
            <select value={changeReason} onChange={e => setChangeReason(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all">
              {CHANGE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={500} placeholder="Optional context" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all resize-none" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button type="button" onClick={saveWithRetry} disabled={!isValid} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#1C2E5E] hover:bg-[#022b80] disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? 'Saving…' : 'Add Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompensationEntryModal;
